import ExcelJS from "exceljs";

export interface ExcelConsignmentData {
  company: any;
  sender: any;
  receiver: any;
  vehicle: any;
  driverInput?: { name?: string; phone?: string; license_number?: string };
  transporter?: any;
  invoiceNumber: string;
  selectedDate: string;
  origin: string;
  destination: string;
  subject?: string;
  items: any[];
  paymentType: string;
  paymentPaidBy?: string;
  selectedBank?: any;
  partyInvoiceNo?: string;
  invoiceValue?: string;
  deliveryAt?: string;
  ewayBillNo?: string;
  roundOff?: string;
  hasBroker?: boolean;
  brokerPaidBy?: string | null;
  notes?: any[];
  description?: string;
}

export async function exportConsignmentToExcel(data: ExcelConsignmentData) {
  const {
    company,
    sender,
    receiver,
    vehicle,
    driverInput,
    invoiceNumber,
    selectedDate,
    origin,
    destination,
    subject,
    items,
    paymentType,
    paymentPaidBy,
    selectedBank,
    partyInvoiceNo,
    invoiceValue,
    deliveryAt,
    ewayBillNo,
    roundOff,
    hasBroker,
    brokerPaidBy,
  } = data;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Consignment_LR");

  ws.columns = [
    { width: 8 },   // A: # / Label
    { width: 28 },  // B: Item Description / Details
    { width: 14 },  // C: HSN Code
    { width: 10 },  // D: UOM
    { width: 14 },  // E: Qty
    { width: 14 },  // F: Rate / Unit
    { width: 16 },  // G: Amount
    { width: 18 },  // H: Extra / Totals
  ];

  const thin: Partial<ExcelJS.Border> = { style: "thin", color: { argb: "FF000000" } };
  const borderAll = { top: thin, bottom: thin, left: thin, right: thin };
  const merge = (row: number, c1: number, c2: number) => ws.mergeCells(row, c1, row, c2);

  let r = 1;

  // 1. GST / PAN / CIN / Risk Row
  ws.getCell(r, 1).value = `GST Enrol No: ${company?.gst_number || "—"}`;
  ws.getCell(r, 1).font = { size: 9, bold: true };
  merge(r, 1, 2);

  ws.getCell(r, 3).value = `PAN No: ${company?.pancard || "—"}`;
  ws.getCell(r, 3).font = { size: 9, bold: true };
  merge(r, 3, 4);

  ws.getCell(r, 5).value = `CIN No: ${company?.cin || "—"}`;
  ws.getCell(r, 5).font = { size: 9, bold: true };
  merge(r, 5, 6);

  ws.getCell(r, 7).value = "AT OWNER'S RISK";
  ws.getCell(r, 7).font = { size: 9, bold: true, color: { argb: "FFCC0000" } };
  ws.getCell(r, 7).alignment = { horizontal: "right" };
  merge(r, 7, 8);
  r++;

  // 2. Company Name
  ws.getCell(r, 1).value = (company?.company_name || "COMPANY NAME").toUpperCase();
  ws.getCell(r, 1).font = { bold: true, size: 16, color: { argb: "FF1A2A5A" } };
  ws.getCell(r, 1).alignment = { horizontal: "center" };
  merge(r, 1, 8); r++;

  // 3. Company Address
  if (company?.address) {
    ws.getCell(r, 1).value = company.address;
    ws.getCell(r, 1).font = { size: 9, color: { argb: "FF444444" } };
    ws.getCell(r, 1).alignment = { horizontal: "center" };
    merge(r, 1, 8); r++;
  }

  // 4. Email & Subject
  ws.getCell(r, 1).value = `Email: ${company?.email || "—"}  |  Subject to ${subject || "Kishangarh"} Jurisdiction`;
  ws.getCell(r, 1).font = { size: 8, italic: true };
  ws.getCell(r, 1).alignment = { horizontal: "center" };
  merge(r, 1, 8); r++;

  // 5. LORRY RECEIPT / CONSIGNMENT NOTE Banner
  ws.getCell(r, 1).value = "LORRY RECEIPT / CONSIGNMENT NOTE";
  ws.getCell(r, 1).font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
  ws.getCell(r, 1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A2A5A" } };
  ws.getCell(r, 1).alignment = { horizontal: "center" };
  merge(r, 1, 8); r++;

  // 6. LR Number & Date
  ws.getCell(r, 1).value = `GR / LR NO: ${invoiceNumber || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 10 };
  merge(r, 1, 4);

  ws.getCell(r, 5).value = `DATED: ${selectedDate || "—"}`;
  ws.getCell(r, 5).font = { bold: true, size: 10 };
  ws.getCell(r, 5).alignment = { horizontal: "right" };
  merge(r, 5, 8); r++;

  // 7. Consignor Block & Route Info
  ws.getCell(r, 1).value = "CONSIGNOR / SUPPLIER:";
  ws.getCell(r, 1).font = { bold: true, size: 8, color: { argb: "FF666666" } };
  merge(r, 1, 4);

  const vehText = vehicle ? `${vehicle.vehicle_number || vehicle.registration_number || "—"}${vehicle.vehicle_type ? ` (${vehicle.vehicle_type})` : ""}` : "—";
  ws.getCell(r, 5).value = `VEHICLE NO: ${vehText}`;
  ws.getCell(r, 5).font = { bold: true, size: 9 };
  merge(r, 5, 8); r++;

  ws.getCell(r, 1).value = `${sender?.name || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 10 };
  merge(r, 1, 4);

  ws.getCell(r, 5).value = `FROM: ${origin || "—"}  ->  TO: ${destination || "—"}`;
  ws.getCell(r, 5).font = { bold: true, size: 9 };
  merge(r, 5, 8); r++;

  if (sender?.address || sender?.number || sender?.gst_number) {
    ws.getCell(r, 1).value = `Address: ${sender?.address || "—"} | Ph: ${sender?.number || "—"}${sender?.gst_number ? ` | GST: ${sender.gst_number}` : ""}`;
    ws.getCell(r, 1).font = { size: 8 };
    merge(r, 1, 4);
  }
  if (driverInput?.name || driverInput?.phone) {
    ws.getCell(r, 5).value = `DRIVER: ${driverInput?.name || "—"} (Ph: ${driverInput?.phone || "—"})`;
    ws.getCell(r, 5).font = { size: 8 };
    merge(r, 5, 8);
  }
  r++;

  // 8. Consignee Block
  ws.getCell(r, 1).value = "CONSIGNEE / RECIPIENT:";
  ws.getCell(r, 1).font = { bold: true, size: 8, color: { argb: "FF666666" } };
  merge(r, 1, 4);

  ws.getCell(r, 5).value = "NOTE: NO CASH ONLY CHEQUE OR POD";
  ws.getCell(r, 5).font = { bold: true, size: 9, color: { argb: "FFCC0000" } };
  ws.getCell(r, 5).alignment = { horizontal: "center" };
  merge(r, 5, 8); r++;

  ws.getCell(r, 1).value = `${receiver?.name || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 10 };
  merge(r, 1, 4); r++;

  if (receiver?.address || receiver?.number || receiver?.gst_number) {
    ws.getCell(r, 1).value = `Address: ${receiver?.address || "—"} | Ph: ${receiver?.number || "—"}${receiver?.gst_number ? ` | GST: ${receiver.gst_number}` : ""}`;
    ws.getCell(r, 1).font = { size: 8 };
    merge(r, 1, 4); r++;
  }
  r++;

  // 9. Items Table Header
  const headerRow = r;
  const headers = ["#", "Product Description", "HSN Code", "UOM", "Qty", "Rate / Unit", "Amount", ""];
  headers.forEach((h, i) => {
    const cell = ws.getCell(headerRow, i + 1);
    cell.value = h;
    cell.font = { bold: true, size: 9, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A2A5A" } };
    cell.alignment = { horizontal: i === 1 ? "left" : (i >= 4 ? "right" : "center") };
    cell.border = borderAll;
  });
  r++;

  // 10. Items Data
  const safeItems = Array.isArray(items) ? items.filter(it => it && typeof it === "object" && (it.name || it.description || it.qty || it.amount)) : [];
  let totalQty = 0;
  let totalAmount = 0;

  safeItems.forEach((item, idx) => {
    const qty = parseFloat(item.qty) || 0;
    const rate = parseFloat(item.ratePerQtl || item.rate) || 0;
    const amt = parseFloat(item.amount) || (qty * rate);
    totalQty += qty;
    totalAmount += amt;

    ws.getCell(r, 1).value = idx + 1;
    ws.getCell(r, 2).value = item.name || item.description || "—";
    ws.getCell(r, 3).value = item.hsnCode || item.hsm_code || "—";
    ws.getCell(r, 4).value = item.uom || "Ton";
    ws.getCell(r, 5).value = qty > 0 ? qty : "—";
    ws.getCell(r, 6).value = rate > 0 ? rate : "—";
    ws.getCell(r, 7).value = amt > 0 ? amt : "—";
    ws.getCell(r, 8).value = "";

    for (let col = 1; col <= 8; col++) {
      const cell = ws.getCell(r, col);
      cell.border = borderAll;
      cell.font = { size: 9 };
      if (col === 1 || col === 3 || col === 4) cell.alignment = { horizontal: "center" };
      if (col >= 5 && col <= 7) cell.alignment = { horizontal: "right" };
    }
    r++;
  });

  if (safeItems.length === 0) {
    ws.getCell(r, 1).value = 1;
    ws.getCell(r, 2).value = "—";
    for (let col = 1; col <= 8; col++) {
      const cell = ws.getCell(r, col);
      cell.border = borderAll;
    }
    r++;
  }

  // 11. Totals
  const roundOffNum = parseFloat(roundOff || "0") || 0;
  const grandTotal = totalAmount + roundOffNum;

  // Subtotal Row
  ws.getCell(r, 1).value = "SUB TOTAL";
  ws.getCell(r, 1).font = { bold: true, size: 9 };
  merge(r, 1, 4);
  ws.getCell(r, 5).value = totalQty > 0 ? totalQty : "";
  ws.getCell(r, 5).font = { bold: true, size: 9 };
  ws.getCell(r, 5).alignment = { horizontal: "right" };
  ws.getCell(r, 7).value = totalAmount > 0 ? totalAmount : "";
  ws.getCell(r, 7).font = { bold: true, size: 9 };
  ws.getCell(r, 7).alignment = { horizontal: "right" };
  for (let col = 1; col <= 8; col++) ws.getCell(r, col).border = borderAll;
  r++;

  if (roundOffNum !== 0) {
    ws.getCell(r, 1).value = "ROUND OFF";
    ws.getCell(r, 1).font = { size: 9 };
    merge(r, 1, 6);
    ws.getCell(r, 7).value = roundOffNum;
    ws.getCell(r, 7).font = { size: 9 };
    ws.getCell(r, 7).alignment = { horizontal: "right" };
    for (let col = 1; col <= 8; col++) ws.getCell(r, col).border = borderAll;
    r++;
  }

  ws.getCell(r, 1).value = "GRAND TOTAL";
  ws.getCell(r, 1).font = { bold: true, size: 10 };
  merge(r, 1, 6);
  ws.getCell(r, 7).value = grandTotal > 0 ? grandTotal : "—";
  ws.getCell(r, 7).font = { bold: true, size: 10 };
  ws.getCell(r, 7).alignment = { horizontal: "right" };
  for (let col = 1; col <= 8; col++) {
    const cell = ws.getCell(r, col);
    cell.border = borderAll;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6F0FA" } };
  }
  r += 2;

  // 12. BANK DETAILS BLOCK (explicitly merged cols 1-5 for max visibility)
  const bankName = selectedBank?.bank_name || sender?.bank_name || "";
  const bankAc = selectedBank?.account_number || sender?.bank_account_number || "";
  const bankIfsc = selectedBank?.ifsc_code || sender?.bank_ifsc || "";
  const bankHolder = selectedBank?.account_holder_name || sender?.account_holder_name || "";
  const bankBranch = selectedBank?.branch || "";
  const bankType = selectedBank?.account_type || "";

  const bankStartRow = r;
  ws.getCell(r, 1).value = "Bank Details (GST WILL BE PAID BY CONSIGNEE):";
  ws.getCell(r, 1).font = { bold: true, size: 10, color: { argb: "FF1A2A5A" } };
  merge(r, 1, 5); r++;

  ws.getCell(r, 1).value = `Bank Name: ${bankName || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 9 };
  merge(r, 1, 5); r++;

  if (bankHolder) {
    ws.getCell(r, 1).value = `A/C Holder Name: ${bankHolder}`;
    ws.getCell(r, 1).font = { size: 9 };
    merge(r, 1, 5); r++;
  }

  ws.getCell(r, 1).value = `A/C NO: ${bankAc || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 9 };
  merge(r, 1, 5); r++;

  ws.getCell(r, 1).value = `IFSC CODE: ${bankIfsc || "—"}`;
  ws.getCell(r, 1).font = { bold: true, size: 9 };
  merge(r, 1, 5); r++;

  if (bankBranch) {
    ws.getCell(r, 1).value = `Branch: ${bankBranch}`;
    ws.getCell(r, 1).font = { size: 9 };
    merge(r, 1, 5); r++;
  }
  if (bankType) {
    ws.getCell(r, 1).value = `Account Type: ${bankType}`;
    ws.getCell(r, 1).font = { size: 9 };
    merge(r, 1, 5); r++;
  }

  // Right Side (cols 6-8): Other Details
  let rr = bankStartRow;
  if (partyInvoiceNo) {
    ws.getCell(rr, 6).value = `PARTY INVOICE NO: ${partyInvoiceNo}`;
    ws.getCell(rr, 6).font = { size: 8 };
    merge(rr, 6, 8); rr++;
  }
  if (invoiceValue) {
    ws.getCell(rr, 6).value = `INVOICE VALUE: ₹${invoiceValue}`;
    ws.getCell(rr, 6).font = { size: 8 };
    merge(rr, 6, 8); rr++;
  }
  if (deliveryAt) {
    ws.getCell(rr, 6).value = `DELIVERY AT: ${deliveryAt}`;
    ws.getCell(rr, 6).font = { size: 8 };
    merge(rr, 6, 8); rr++;
  }
  if (ewayBillNo) {
    ws.getCell(rr, 6).value = `E-WAY BILL NO: ${ewayBillNo}`;
    ws.getCell(rr, 6).font = { size: 8 };
    merge(rr, 6, 8); rr++;
  }
  ws.getCell(rr, 6).value = `PAYMENT MODE: ${(paymentType || "").toUpperCase()} (Paid By: ${(paymentPaidBy || "receiver").toUpperCase()})`;
  ws.getCell(rr, 6).font = { bold: true, size: 8 };
  merge(rr, 6, 8); rr++;

  if (hasBroker && brokerPaidBy) {
    ws.getCell(rr, 6).value = `BROKER COMMISSION PAID BY: ${brokerPaidBy.toUpperCase()}`;
    ws.getCell(rr, 6).font = { size: 8, italic: true };
    merge(rr, 6, 8); rr++;
  }

  r = Math.max(r, rr) + 1;

  // 13. Terms & Signature Block
  ws.getCell(r, 1).value = "TERMS & CONDITIONS:";
  ws.getCell(r, 1).font = { bold: true, size: 8 };
  merge(r, 1, 5);

  ws.getCell(r, 6).value = `For ${company?.company_name || "COMPANY NAME"}`;
  ws.getCell(r, 6).font = { bold: true, size: 9 };
  ws.getCell(r, 6).alignment = { horizontal: "right" };
  merge(r, 6, 8); r++;

  ws.getCell(r, 1).value = "1. Carrier not responsible for leakage, breakage, or water damage.\n2. Goods booked at owner's risk.\nE. & O.E.";
  ws.getCell(r, 1).font = { size: 8, color: { argb: "FF555555" } };
  ws.getCell(r, 1).alignment = { wrapText: true };
  merge(r, 1, 5);

  ws.getCell(r, 6).value = "Authorised Signatory";
  ws.getCell(r, 6).font = { size: 8 };
  ws.getCell(r, 6).alignment = { horizontal: "right" };
  merge(r, 6, 8); r += 2;

  ws.getCell(r, 1).value = "THIS IS A COMPUTER GENERATED LR. DOES NOT REQUIRE SIGNATURE.";
  ws.getCell(r, 1).font = { size: 8, italic: true, color: { argb: "FF777777" } };
  ws.getCell(r, 1).alignment = { horizontal: "center" };
  merge(r, 1, 8);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = String(company?.company_name || "LR").replace(/[^\w\-]+/g, "_");
  const safeInv = String(invoiceNumber || "Invoice").replace(/[^\w\-]+/g, "_");
  a.download = `Consignment_${safeName}_${safeInv}_${Date.now()}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
